import { Icon } from '@clay-run/glaze/icon';
import { useLocation } from 'react-router-dom';

import { FLOW_ICONS } from '../../icons';
import { useRailNavigate } from '../rail/rail-items';
import { openPanel, setFlowClosed } from './flow-panels-store';

/**
 * What the Flow column shows while `flowClosed` is true but the column has
 * NOT actually collapsed (R7 — i.e. anything other than "both side panels
 * open and pushing"). Rendered as a sibling of the hidden `children`
 * wrapper inside `FlowPanels`' Flow column, so the real Flow content stays
 * mounted underneath it (D8).
 *
 * The three labels and descriptions are VERBATIM from the interaction spec —
 * no colons, no trailing periods. Do not reword them without the spec
 * changing first.
 *
 * ── Discover vs. Act (R5) ────────────────────────────────────────────────
 * Discover only clears `flowClosed` — Flow then reopens on whatever `?tab=`
 * is already in the URL, not a hardcoded subtab (no `setSubtab` call here;
 * see plan 2026-10-06-002's Resolved Question 2 — a one-line follow-up if
 * Spencer wants it to always land on Companies instead).
 *
 * Act opens the Actions panel via `openPanel('actions')` and does NOT touch
 * `flowClosed` — R4 reserves writing `flowClosed` to the Header's own Flow
 * toggle alone. If Chat is also open, Flow then collapses on its own
 * because `deriveFlowPanelsLayout`'s three-flag rule says so once Actions
 * joins Chat — that's expected (Spencer's call), not a bug.
 *
 * `plans/2026-09-30-005-v2-panel-container-plan.md:38`'s R7 (Act routes to
 * the Plays subtab via an `onAct` prop) is superseded by this plan's R5 —
 * Act now opens the Actions panel instead, and the `onDiscover`/`onAct`
 * props this component used to take are gone end to end.
 *
 * Off `/flow/*` (Discover is app-wide) it also navigates to the Flow tables on
 * the last subtab.
 *
 * ── Why Chat does not clear `flowClosed` (D11, R4) ──────────────────────
 * The spec says "Chat opens the chat panel", nothing more, and R4 reserves
 * `flowClosed` for the Header's own Flow toggle. So Chat calls `openPanel`
 * only; if Actions happens to be open too, Flow then collapses on its own
 * because `deriveFlowPanelsLayout` says so. Discover DOES clear
 * `flowClosed`, because its whole point is to put the rep back in the Flow
 * column on whatever tab is already selected.
 */
export function FlowEmptyState() {
  const { pathname } = useLocation();
  const { goFlow } = useRailNavigate();
  const rows = [
    {
      icon: FLOW_ICONS.chat,
      label: 'Chat',
      description: 'Ask about an account, a person, or a play',
      onClick: () => openPanel('chat'),
    },
    {
      icon: FLOW_ICONS.search,
      label: 'Discover',
      description: 'Find accounts and people worth working',
      // Off Flow, Discover lands on the Flow tables (last subtab) instead of
      // just revealing the current page again.
      onClick: () => (pathname.startsWith('/flow') ? setFlowClosed(false) : goFlow()),
    },
    {
      icon: FLOW_ICONS.actions,
      label: 'Act',
      description: 'Enroll contacts and send from a play',
      onClick: () => openPanel('actions'),
    },
  ];

  return (
    <div
      data-flow-empty-state
      className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-8"
    >
      <div className="flex w-full max-w-md flex-col gap-4">
        <h2 className="text-center text-base font-medium text-content-primary">
          What would you like to do?
        </h2>
        <div className="flex flex-col gap-2">
          {rows.map((row) => (
            <button
              key={row.label}
              type="button"
              onClick={row.onClick}
              className="flex items-center gap-3 rounded-lg border border-border-primary bg-bg-primary px-4 py-3 text-left transition-colors hover:bg-bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-action"
            >
              <Icon type={row.icon} size="sm" aria-hidden className="text-content-tertiary" />
              <span className="flex min-w-0 flex-col">
                <span className="text-sm font-medium text-content-primary">{row.label}</span>
                <span className="text-xs text-content-secondary">{row.description}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
